import { LegendList, type LegendListProps, type LegendListRef } from "@-/com.npmjs.legendapp__list";
import useLatest from "@-/com.npmjs.use-latest";
import { useCallback, useEffect, useRef, type Ref } from "react";
import { Platform } from "@-/com.npmjs.react-native";
import { useHeaderScrollContentStyle } from "#header-scroll-content-style";
import { useGlassHeaderViewport } from "#use-glass-header-viewport";
import {
  type HeaderLegendListScrollPosition,
  useHeaderLegendListScrollPosition,
} from "#use-header-legend-list-scroll-position";

/** The virtualized counterpart of HeaderScrollView; preserves item types and the list ref. */
export function HeaderLegendList<Item>({
  glassHeaderOverlap,
  headerOverlap,
  initialScrollIndex,
  ListHeaderComponent,
  onNearEndChange,
  onScroll,
  recycleItems = false,
  ref,
  scrollIndicatorInsets,
  scrollPosition,
  style,
  suppressContentGap,
  testID,
  ...props
}: LegendListProps<Item> & {
  /** See `useGlassHeaderViewport`: the floating glass chrome's height on iOS 26. */
  glassHeaderOverlap?: number;
  headerOverlap?: number;
  /**
   * Whether the end of the content is within `onEndReachedThreshold` of the viewport, reported on
   * mount and on every change. Unlike `onEndReached`, which latches once it fires until the
   * learner scrolls away, this stays true while the end is still in reach after more data lands.
   */
  onNearEndChange?: (isNearEnd: boolean) => void;
  ref?: Ref<LegendListRef>;
  scrollPosition?: HeaderLegendListScrollPosition;
  suppressContentGap?: boolean;
}) {
  const glass = useGlassHeaderViewport({
    glassHeaderOverlap,
    horizontal: props.horizontal,
    scrollIndicatorInsets,
    style,
  });
  const contentContainerStyle = useHeaderScrollContentStyle({
    ...props,
    headerOverlap: glass.headerOverlap ?? headerOverlap,
    // No fade to clear beneath glass chrome: its own gutter is the resting gap.
    suppressContentGap: suppressContentGap === true || glass.isActive,
  });
  const listRef = useRef<LegendListRef | null>(null);
  const setListRef = useCallback(
    (instance: LegendListRef | null) => {
      listRef.current = instance;
      if (typeof ref === "function") {
        ref(instance);
      } else if (ref != null) {
        ref.current = instance;
      }
    },
    [ref],
  );
  const position = useHeaderLegendListScrollPosition<Item>({
    contentContainerStyle,
    data: props.data,
    initialScrollIndex,
    keyExtractor: props.keyExtractor,
    ListHeaderComponent,
    onScroll,
    ref: setListRef,
    scrollPosition,
    style: glass.style,
  });
  const onNearEndChangeRef = useLatest(onNearEndChange);
  const hasNearEndListener = onNearEndChange !== undefined;
  useEffect(() => {
    const state = hasNearEndListener ? listRef.current?.getState() : undefined;
    if (state === undefined) {
      return;
    }
    onNearEndChangeRef.current?.(state.isNearEnd);
    return state.listen("isNearEnd", (isNearEnd) => onNearEndChangeRef.current?.(isNearEnd));
  }, [hasNearEndListener, onNearEndChangeRef]);
  // Legend List's web entry renders a DOM scroll container directly, which receives every prop:
  // only DOM-safe names there (native scroll indicator insets would reach the DOM element).
  const platformProps =
    Platform.OS === "web"
      ? { "data-testid": testID }
      : { scrollIndicatorInsets: glass.scrollIndicatorInsets, testID };
  return (
    <LegendList<Item>
      {...props}
      {...platformProps}
      contentContainerStyle={contentContainerStyle}
      initialScrollIndex={position.initialScrollIndex}
      ListHeaderComponent={position.ListHeaderComponent}
      onScroll={position.onScroll}
      recycleItems={recycleItems}
      ref={position.ref}
      style={glass.style}
    />
  );
}
